import { useEffect, useState } from 'react';
import type { BalanceType } from '../types/BalanceType';
import { getBalanceTypes } from '../api/balanceTypes';

// Module-level cache shared across the page's lifetime. Fetched LAZILY (first
// hook use); cleared on logout via clearBalanceTypesCache(). Mirrors useCrcies.
let cache: BalanceType[] | null = null;
let inflight: Promise<BalanceType[]> | null = null;

function load(): Promise<BalanceType[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getBalanceTypes()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearBalanceTypesCache(): void {
  cache = null;
  inflight = null;
}

interface UseBalanceTypesResult {
  data: BalanceType[] | null;
  loading: boolean;
  error: string | null;
}

export function useBalanceTypes(): UseBalanceTypesResult {
  const [state, setState] = useState<UseBalanceTypesResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }
    let cancelled = false;
    load()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
